import { ArrowRight, Check, Settings } from "lucide-react";
import dayjs from "dayjs";
import { motion } from "framer-motion";
import { cn } from "utils/cn";
import { Button } from "components/common/buttons/Button";
import { ProjectState } from "hooks/useProjects/useProjects.types";
import { getNextStep, getPipelineStages } from "../../ProjectsPage.constants";
import { ProjectPoster } from "../ProjectPoster";
import { ProjectTeamStack, getTeamStackMembers } from "../ProjectTeamStack";
import { ProjectBadges } from "./ProjectBadges";

export interface ProjectCardProps {
  project: ProjectState;
  index: number;
  onOpenProject: (projectId: string, path?: string) => void;
  onProjectSettingsClick: (projectId: string) => void;
}

export const ProjectCard = ({
  project,
  index,
  onOpenProject,
  onProjectSettingsClick,
}: ProjectCardProps) => {
  const stages = getPipelineStages(project);
  const stagesDone = stages.filter((stage) => stage.done).length;
  const nextStep = getNextStep(project);
  const started = project.createdAt ? dayjs(project.createdAt).fromNow() : "";

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.28, delay: Math.min(index * 0.03, 0.3), ease: "easeOut" }}
      className={cn(
        "group relative flex flex-col overflow-hidden rounded-xl border border-border bg-card transition-all duration-200",
        project.archived
          ? "opacity-70"
          : "hover:-translate-y-0.5 hover:border-foreground/20 hover:shadow-lg"
      )}
    >
      <button
        type="button"
        onClick={() => onOpenProject(project.projectId)}
        disabled={project.archived}
        aria-label={`Open ${project.name}`}
        className="flex flex-col text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/50 disabled:cursor-not-allowed"
      >
        <ProjectPoster project={project} className="aspect-[2/1] w-full border-b border-border" />
        <span className="flex min-h-11 min-w-0 items-center gap-2 px-4 py-3 text-xs text-muted-foreground">
          {started && <span className="shrink-0">Started {started}</span>}
          {(project.versionsCount ?? 0) > 1 && (
            <span className="shrink-0">· {project.versionsCount} drafts</span>
          )}
          <ProjectTeamStack members={getTeamStackMembers(project)} className="ml-auto" />
        </span>
      </button>

      {!project.archived && (
        <div className="mt-auto flex items-center gap-3 border-t border-border/70 px-4 py-2.5">
          <span
            className="flex w-16 shrink-0 gap-0.5"
            role="img"
            aria-label={`${stagesDone} of ${stages.length} stages complete`}
          >
            {stages.map((stage) => (
              <span
                key={stage.label}
                title={stage.done ? `${stage.label} complete` : `${stage.label} not complete yet`}
                className={cn(
                  "h-1 flex-1 rounded-full",
                  stage.done ? "bg-foreground/60" : "bg-muted"
                )}
              />
            ))}
          </span>
          <Button
            type="button"
            color="gray"
            size="xs"
            onClick={() => onOpenProject(project.projectId, nextStep.path)}
            className="ml-auto min-w-0 gap-1.5 [&_svg]:h-3.5 [&_svg]:w-3.5"
          >
            {nextStep.allDone ? <Check className="text-success" aria-hidden /> : null}
            <span className="truncate">
              {nextStep.allDone ? "All stages complete" : `Next: ${nextStep.title}`}
            </span>
            <ArrowRight className="transition-transform group-hover:translate-x-0.5" aria-hidden />
          </Button>
        </div>
      )}

      <div className="pointer-events-none absolute inset-x-3 top-4 flex items-start justify-between gap-2">
        <span />
        <span className="flex items-center gap-1.5">
          <ProjectBadges project={project} />
          <Button
            type="button"
            color="gray"
            size="icon"
            onClick={() => onProjectSettingsClick(project.projectId)}
            className="pointer-events-auto h-7 w-7 rounded-md opacity-0 shadow-sm backdrop-blur-sm hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100 [&_svg]:h-3.5 [&_svg]:w-3.5"
            title="Project settings"
            aria-label={`Open settings for ${project.name}`}
          >
            <Settings />
          </Button>
        </span>
      </div>
    </motion.div>
  );
};
